<template>
  <div ref="container" class="three-container"></div>
</template>

<script>
import * as THREE from 'three'

export default {
  name: 'ParticleBackground',
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      animationId: null,
      clock: null,
      // 主体螺旋粒子组
      streamGroups: [],
      // 背景星空
      starField: null,
      // 消散拖尾粒子
      trailGroups: [],
      width: 0,
      height: 0
    }
  },
  mounted() {
    this.init()
    this.animate()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    cancelAnimationFrame(this.animationId)
    window.removeEventListener('resize', this.handleResize)
    this.renderer.dispose()
    this.scene.traverse(obj => {
      if (obj.geometry) obj.geometry.dispose()
      if (obj.material) {
        if (obj.material.map) obj.material.map.dispose()
        obj.material.dispose()
      }
    })
  },
  methods: {
    init() {
      const container = this.$refs.container
      this.width = window.innerWidth
      this.height = window.innerHeight

      // 场景
      this.scene = new THREE.Scene()
      // 深邃星空蓝黑背景
      this.scene.background = new THREE.Color(0x020810)
      this.scene.fog = new THREE.FogExp2(0x020810, 0.00008)

      // 相机 — 放在隧道"内部"，看向前方
      this.camera = new THREE.PerspectiveCamera(
        75,
        this.width / this.height,
        0.1,
        2000
      )
      this.camera.position.set(0, 0, -5)
      this.camera.lookAt(0, 0, 50)

      // 渲染器
      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.renderer.setSize(this.width, this.height)
      this.renderer.setClearColor(0x020810, 1)
      container.appendChild(this.renderer.domElement)

      this.clock = new THREE.Clock()

      // 创建光晕贴图
      const glowTexture = this.createGlowTexture()

      // 创建主体螺旋通道
      this.createSpiralStreams(glowTexture)

      // 创建拖尾消散粒子
      this.createTrailParticles(glowTexture)

      // 创建背景星空
      this.createStarField()
    },

    // 创建径向渐变光晕贴图
    createGlowTexture() {
      const size = 128
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')

      const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
      gradient.addColorStop(0.05, 'rgba(255, 255, 255, 0.9)')
      gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.6)')
      gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.2)')
      gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.03)')
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, size, size)

      const texture = new THREE.CanvasTexture(canvas)
      texture.needsUpdate = true
      return texture
    },

    // 霓虹色插值器 #00F2FE → #4FACFE → #FA709A
    neonColor(t) {
      t = Math.max(0, Math.min(1, t))
      let r, g, b
      if (t < 0.5) {
        // #00F2FE → #4FACFE
        const s = t / 0.5
        r = THREE.MathUtils.lerp(0, 79, s)
        g = THREE.MathUtils.lerp(242, 172, s)
        b = THREE.MathUtils.lerp(254, 254, s)
      } else {
        // #4FACFE → #FA709A
        const s = (t - 0.5) / 0.5
        r = THREE.MathUtils.lerp(79, 250, s)
        g = THREE.MathUtils.lerp(172, 112, s)
        b = THREE.MathUtils.lerp(254, 154, s)
      }
      return new THREE.Color(r / 255, g / 255, b / 255)
    },

    // 创建主体螺旋隧道粒子流
    createSpiralStreams(glowTexture) {
      const armCount = 10     // 螺旋臂数量
      const particlesPerArm = 400  // 每条臂的粒子数
      const maxZ = 400        // 隧道深度
      const minZ = 2          // 最近距离
      const turnsPerArm = 4   // 每臂旋转圈数

      for (let a = 0; a < armCount; a++) {
        const baseAngle = (a / armCount) * Math.PI * 2
        const count = particlesPerArm

        const geometry = new THREE.BufferGeometry()
        const positions = new Float32Array(count * 3)
        const colors = new Float32Array(count * 3)
        const sizes = new Float32Array(count)

        // 预计算初始位置（实际每帧会动态更新）
        for (let i = 0; i < count; i++) {
          const t = i / count
          const idx3 = i * 3

          // Z 轴：非线性分布，近处密远处疏
          const z = minZ + Math.pow(t, 0.7) * (maxZ - minZ)

          // 螺旋角度：z 越大旋转越多
          const angle = baseAngle + (z / maxZ) * Math.PI * 2 * turnsPerArm

          // 基础半径随 z 增大（漏斗形）
          const baseRadius = z * 0.7 + 3

          positions[idx3] = Math.cos(angle) * baseRadius     // x
          positions[idx3 + 1] = Math.sin(angle) * baseRadius // y
          positions[idx3 + 2] = z                             // z

          // 霓虹渐变色
          const color = this.neonColor(t)
          colors[idx3] = color.r
          colors[idx3 + 1] = color.g
          colors[idx3 + 2] = color.b

          // 粒子大小：近大远小
          sizes[i] = (1.5 + t * 0.5) * (1 - t * 0.6)
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

        const material = new THREE.PointsMaterial({
          map: glowTexture,
          size: 0.6,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: true,
          transparent: true,
          opacity: 0.85,
          vertexColors: true,
          sizeAttenuation: true
        })

        const points = new THREE.Points(geometry, material)
        points.userData = {
          armIndex: a,
          baseAngle,
          count,
          minZ,
          maxZ,
          turnsPerArm,
          // 每臂独立的波动参数
          waveFreq: 0.015 + Math.random() * 0.02,
          waveAmp: 3 + Math.random() * 5,
          waveSpeed: 0.6 + Math.random() * 1.2,
          wavePhase: Math.random() * Math.PI * 2,
          // 脉动参数
          pulseFreq: 0.04 + Math.random() * 0.03,
          pulseAmp: 1.5 + Math.random() * 2
        }

        this.scene.add(points)
        this.streamGroups.push(points)
      }
    },

    // 拖尾消散粒子 — 散布在隧道深处的光环
    createTrailParticles(glowTexture) {
      const ringCount = 8
      const particlesPerRing = 150

      for (let r = 0; r < ringCount; r++) {
        const geometry = new THREE.BufferGeometry()
        const count = particlesPerRing
        const positions = new Float32Array(count * 3)
        const colors = new Float32Array(count * 3)
        const sizes = new Float32Array(count)

        const ringZ = 50 + r * 45 // 各光环的深度位置
        const ringRadius = ringZ * 0.7 + 5

        for (let i = 0; i < count; i++) {
          const idx3 = i * 3
          const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
          // 在环形区域内随机分布
          const rad = ringRadius + (Math.random() - 0.5) * ringRadius * 0.5
          const z = ringZ + (Math.random() - 0.5) * 15

          positions[idx3] = Math.cos(angle) * rad
          positions[idx3 + 1] = Math.sin(angle) * rad
          positions[idx3 + 2] = z

          const t = r / ringCount
          const color = this.neonColor(0.4 + t * 0.5)
          colors[idx3] = color.r
          colors[idx3 + 1] = color.g
          colors[idx3 + 2] = color.b

          // 越远越大越淡（模拟消散）
          sizes[i] = (2 + Math.random() * 3) * (0.5 + t * 0.5)
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

        const material = new THREE.PointsMaterial({
          map: glowTexture,
          size: 0.35,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: true,
          transparent: true,
          opacity: 0.35 + (r / ringCount) * 0.15,
          vertexColors: true,
          sizeAttenuation: true
        })

        const points = new THREE.Points(geometry, material)
        points.userData = {
          ringIndex: r,
          ringZ,
          ringRadius,
          rotateSpeed: 0.08 + Math.random() * 0.12
        }
        this.scene.add(points)
        this.trailGroups.push(points)
      }
    },

    // 背景星空
    createStarField() {
      const count = 1500
      const geometry = new THREE.BufferGeometry()
      const positions = new Float32Array(count * 3)
      const colors = new Float32Array(count * 3)

      for (let i = 0; i < count; i++) {
        const idx3 = i * 3
        // 球形随机分布，半径很大
        const phi = Math.acos(2 * Math.random() - 1)
        const theta = Math.random() * Math.PI * 2
        const radius = 800 + Math.random() * 400

        positions[idx3] = radius * Math.sin(phi) * Math.cos(theta)
        positions[idx3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
        positions[idx3 + 2] = radius * Math.cos(phi)

        // 蓝白微色
        const brightness = 0.3 + Math.random() * 0.7
        colors[idx3] = 0.6 * brightness
        colors[idx3 + 1] = 0.75 * brightness
        colors[idx3 + 2] = brightness
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

      const material = new THREE.PointsMaterial({
        size: 1.2,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        transparent: true,
        opacity: 0.7,
        vertexColors: true,
        sizeAttenuation: true
      })

      this.starField = new THREE.Points(geometry, material)
      this.starField.userData = { rotateSpeed: 0.015 }
      this.scene.add(this.starField)
    },

    // 更新螺旋粒子位置（波动+脉动效果）
    updateStreamArm(arm, elapsed) {
      const geometry = arm.geometry
      const posArr = geometry.attributes.position.array
      const sizeArr = geometry.attributes.size.array
      const ud = arm.userData
      const { baseAngle, count, minZ, maxZ, turnsPerArm, waveFreq, waveAmp, waveSpeed, wavePhase, pulseFreq, pulseAmp } = ud

      for (let i = 0; i < count; i++) {
        const t = i / count
        const idx3 = i * 3

        let z = minZ + Math.pow(t, 0.7) * (maxZ - minZ)
        // 让粒子沿 Z 轴流动
        z += elapsed * 25
        if (z > maxZ) z = minZ + (z - maxZ)
        if (z < minZ) z = maxZ

        const angle = baseAngle + (z / maxZ) * Math.PI * 2 * turnsPerArm
        let baseRadius = z * 0.7 + 3

        // 能量波动：正弦调制半径
        const wave = Math.sin(z * waveFreq - elapsed * waveSpeed + wavePhase) * waveAmp
        const wave2 = Math.cos(z * waveFreq * 1.7 - elapsed * waveSpeed * 0.6 + wavePhase) * waveAmp * 0.4
        baseRadius += wave + wave2

        // 脉动（时间维度的膨胀收缩）
        const pulse = 1 + Math.sin(elapsed * pulseFreq) * pulseAmp
        baseRadius *= pulse

        posArr[idx3] = Math.cos(angle) * baseRadius
        posArr[idx3 + 1] = Math.sin(angle) * baseRadius
        posArr[idx3 + 2] = z

        // 动态大小
        const zNorm = (z - minZ) / maxZ
        sizeArr[i] = (1.5 + zNorm * 0.5) * (1 - zNorm * 0.6) * pulse
      }

      geometry.attributes.position.needsUpdate = true
      geometry.attributes.size.needsUpdate = true
    },

    // 更新拖尾光环
    updateTrailRing(ring, elapsed) {
      const geometry = ring.geometry
      const posArr = geometry.attributes.position.array
      const sizeArr = geometry.attributes.size.array
      const { rotateSpeed } = ring.userData
      const count = geometry.attributes.position.count

      for (let i = 0; i < count; i++) {
        const idx3 = i * 3
        const x = posArr[idx3]
        const y = posArr[idx3 + 1]

        const cosA = Math.cos(rotateSpeed * 0.016)
        const sinA = Math.sin(rotateSpeed * 0.016)
        posArr[idx3] = x * cosA - y * sinA
        posArr[idx3 + 1] = x * sinA + y * cosA

        // 大小脉动
        sizeArr[i] = sizeArr[i] * (1 + Math.sin(elapsed * 2 + i * 0.1) * 0.1)
      }

      geometry.attributes.position.needsUpdate = true
      geometry.attributes.size.needsUpdate = true
    },

    handleResize() {
      this.width = window.innerWidth
      this.height = window.innerHeight
      this.camera.aspect = this.width / this.height
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(this.width, this.height)
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate)

      const elapsed = this.clock.getElapsedTime()

      // 相机微动（模拟飞船微调姿态）
      const camShake = 0.3
      this.camera.position.x = Math.sin(elapsed * 0.3) * camShake
      this.camera.position.y = Math.cos(elapsed * 0.4) * camShake
      this.camera.lookAt(0, Math.sin(elapsed * 0.2) * 3, 200)

      // 更新螺旋粒子
      this.streamGroups.forEach(arm => this.updateStreamArm(arm, elapsed))

      // 更新拖尾光环
      this.trailGroups.forEach(ring => this.updateTrailRing(ring, elapsed))

      // 旋转星空
      if (this.starField) {
        this.starField.rotation.z += 0.0002
        this.starField.rotation.y += 0.0001
      }

      this.renderer.render(this.scene, this.camera)
    }
  }
}
</script>

<style scoped>
.three-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.three-container canvas {
  display: block;
}
</style>
